import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { BASE_TIMEZONE } from '~/config';

// Extend dayjs with all required plugins
dayjs.extend(quarterOfYear);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekOfYear);
dayjs.extend(relativeTime);

// Get the current quarter
export const REPORT_PERIODS = [
  'Last 7 Days',
  'This Month',
  'Last Month',
  'Current Quarter',
  'Last Quarter',
  'Year To Date',
] as const;

export const getStartAndDateBasedOnPeriod = (period: (typeof REPORT_PERIODS)[number]) => {
  if (period === 'Last 7 Days') {
    return {
      startDate: dayjs().subtract(7, 'day'),
      endDate: dayjs(),
    };
  }
  if (period === 'This Month') {
    return {
      startDate: dayjs().startOf('month'),
      endDate: dayjs().endOf('month'),
    };
  }
  if (period === 'Last Month') {
    const lastMonth = dayjs().startOf('month').subtract(1, 'day');
    return {
      startDate: lastMonth.startOf('month'),
      endDate: lastMonth.endOf('month'),
    };
  }
  if (period === 'Current Quarter') {
    return {
      startDate: dayjs().startOf('quarter'),
      endDate: dayjs().endOf('quarter'),
    };
  }
  if (period === 'Last Quarter') {
    const lastQuarterStart = dayjs().startOf('quarter').subtract(1, 'quarter');
    return {
      startDate: lastQuarterStart.startOf('quarter'),
      endDate: lastQuarterStart.endOf('quarter'),
    };
  }
  return {
    startDate: dayjs().startOf('year'),
    endDate: dayjs(),
  };
};

/**
 * Returns an array of objects representing the weeks in a given month and year.
 * Each object contains the start and end dates of a week, ensuring that the
 * weeks do not start before the first day of the month or end after the last day of the month.
 *
 * @param {number} year - The year for which to get the weeks.
 * @param {number} month - The month (1-12) for which to get the weeks.
 * @return {{ start: Date, end: Date }[]} An array of objects with start and end dates for each week.
 */
export const getWeeksOfMonth = (year: number, month: number) => {
  const weeks = [];
  // Set the first and last day of the month in the base tz
  let start = dayjs.tz(`${year}-${month}-01`, BASE_TIMEZONE).startOf('day');
  let end = start.endOf('month');
  let currentWeekStart = start.startOf('week'); // Align to the start of the week (Sunday)
  while (currentWeekStart.isBefore(end)) {
    let currentWeekEnd = currentWeekStart.endOf('week');
    // Ensure week does not start before the first day of the month
    if (currentWeekStart.isBefore(start)) {
      currentWeekStart = start;
    }
    // Ensure week does not end after the last day of the month
    if (currentWeekEnd.isAfter(end)) {
      currentWeekEnd = end;
    }
    weeks.push({
      start: currentWeekStart.toDate(),
      end: currentWeekEnd.toDate(),
    });
    // Move to the next week's start (Sunday)
    currentWeekStart = currentWeekStart.add(1, 'week').startOf('week');
  }
  return weeks;
};

/**
 * Helper functions for formatting dates in a human-readable way
 */
export const dateFormatters = {
  /**
   * Returns a human-readable relative time (e.g., "2 hours ago", "yesterday", "in 3 days")
   *
   * @param {Date | string | number} date - The date to format
   * @return {string} A human-readable relative time string
   */
  relativeTime: (date: Date | string | number) => {
    return dayjs(date).fromNow();
  },

  /**
   * Returns a formatted date string with time
   *
   * @param {Date | string | number} date - The date to format
   * @param {string} format - The format string (default: 'MMM DD, YYYY h:mm A')
   * @return {string} A formatted date string
   */
  fullDateTime: (date: Date | string | number, format = 'MMM DD, YYYY h:mm A') => {
    return dayjs(date).format(format);
  },

  /**
   * Returns a formatted date string without time
   *
   * @param {Date | string | number} date - The date to format
   * @param {string} format - The format string (default: 'MMM DD, YYYY')
   * @return {string} A formatted date string
   */
  dateOnly: (date: Date | string | number, format = 'MMM DD, YYYY') => {
    return dayjs(date).format(format);
  },
};
